import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { Timer, Lock } from 'lucide-react';

import { Button } from '@components/ui/button';
import { Separator } from '@components/ui/separator';
import { Heading } from '@components/ui/typography/heading';
import { Text } from '@components/ui/typography/text';
import { VStack, HStack } from '@components/ui/layout/flex';

interface LandingProps {
  setShowWizard: (show: boolean) => void;
  title?: string;
  description?: string;
  duration: number;
  branding?: {
    name?: string;
    logo?: string;
    fontColor?: string;
    buttonColor?: string;
  };
}

export function Landing({ setShowWizard, title, description, duration, branding }: LandingProps) {
  const { t } = useTranslation();

  if (!branding) return null;

  return (
    <VStack
      role="application"
      className="bg-white max-w-lg p-7 rounded-[15px] text-center"
      aria-label={`Welcome to ${branding.name ?? ''}'s form.`}
      gap={0}
    >
      <img
        className="p-1 w-full h-[100px] object-contain"
        alt={`${branding.name ?? ''}'s Logo`}
        src={branding.logo ?? ''}
      />
      <Separator aria-hidden="true" className="mt-4" />

      <div className="py-4">
        <Heading level={4} className="mt-2 pb-2 font-medium">{title}</Heading>
        <Text className="font-normal">{description}</Text>
      </div>

      <Button
        type="button"
        className="w-4/5 hover:-translate-y-0.5 hover:shadow-lg transition-all"
        style={{
          color: branding?.fontColor,
          backgroundColor: branding?.buttonColor,
        }}
        onClick={() => setShowWizard(true)}
      >
        {t('form.buttons.start')}
      </Button>

      <div className="py-4">
        <HStack align="center" justify="center" gap={2}>
          <Timer size={16} />
          <Text>{t('form.duration', { duration: duration })}</Text>
        </HStack>

        <HStack className="pt-2" align="center" justify="center" gap={2}>
          <Lock size={16} />
          <Text>{t('form.end-to-end')}</Text>
        </HStack>
      </div>

      <Separator aria-hidden="true" />
      <Text size="sm" className="pt-3">
        {t('form.confirmation')}{' '}
        <Link className="text-teal-700" to="/privacy" aria-label={t('aria.terms')}>{t('form.terms')}</Link>
        {' '}{t('and')}{' '}
        <Link className="text-teal-700" to="/privacy" aria-label={t('aria.privacy')}>{t('form.privacy')}</Link>
      </Text>
      <Separator aria-hidden="true" />
    </VStack>
  );
}
